/* istanbul ignore file */

import _ from 'lodash'
import { SettingsIcon } from '@panwds/icons'
import { MSP_MICRO_APP, ASC_TIER_1_SUPPORT_ROLE } from './common/constants/GlobalConstants'
import type { NavParams, NavItem, StoreState } from '@sparky/framework'
import type { CommonModuleState } from './common/types'

// temp solution: remove when aiops-free become a separate shell
const AIOPS_FREE_PREFIX = '/aiops-free'
const notAiopsFree = () => !window.location.pathname.startsWith(AIOPS_FREE_PREFIX)
const commonServicesAvail = {
  avail: ({ access }: any) => notAiopsFree() && !access?.hasRoleOnly?.(ASC_TIER_1_SUPPORT_ROLE),
  availFallbackRedirect: () => (notAiopsFree() ? undefined : AIOPS_FREE_PREFIX),
}

const microApp = ({ vars, sparkyFramework }: NavParams) => {
  if (!sparkyFramework.hasTsgSupport()) {
    return null
  }

  sparkyFramework.actions.loadApps().catch(() => { })

  const importComponent = (name: string, { layout }: { layout?: string } = {}) => {
    const fn = () => import('./components').then((module: any) => ({ default: module[name] }))
    fn.lazy = true
    if (layout) {
      fn.layout = layout
    }
    return fn
  }
  const hubStateProps = ({ auth }: StoreState) => ({ vars, auth })
  const commonModuleStateProps = ({ auth, commonModule, theme }: StoreState) => ({ vars, auth, commonModule: commonModule as CommonModuleState, theme })
  const inHub = sparkyFramework.getFrameworkVar('hub')
  const isNetSec = sparkyFramework.isOidcClient(sparkyFramework.getFrameworkVar('netsec_client_id') || 'strata')

  const getCommonServicePages = (): NavItem[] => {
    if (!sparkyFramework.getFrameworkVar('use_new_left_nav')) {
      return []
    }

    const pages: NavItem[] = [
      {
        key: 'subscriptions',
        path: '/settings/subscriptions',
        title: 'Subscriptions',
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: importComponent('Subscriptions'),
        mapStateToProps: commonModuleStateProps,
        ...commonServicesAvail,
      },
      {
        key: 'tenants',
        path: '/settings/tenants',
        title: 'Tenants',
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: importComponent('TenantManagement'),
        mapStateToProps: commonModuleStateProps,
        ...commonServicesAvail,
      },
      {
        key: 'das',
        path: '/settings/devices',
        title: 'Device Associations',
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: importComponent('DevicesAssociations'),
        mapStateToProps: commonModuleStateProps,
        ...commonServicesAvail,
      },
      {
        key: 'iam',
        path: '/settings/iam',
        title: 'Identity & Access',
        expandable: false,
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: importComponent('IAM', { layout: 'tabs' }),
        mapStateToProps: commonModuleStateProps,
        ...commonServicesAvail,
      },
      (vars.ip_allow_list ? (
        {
          key: 'ip_restrictions',
          path: '/settings/ip',
          title: 'Trusted IP List',
          contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
          component: importComponent('IpAllowList'),
          mapStateToProps: commonModuleStateProps,
          ...commonServicesAvail,
          avail: ({ access, auth }: any) => {
            const { tsg_id, tsgs } = auth
            const ipAllowTSGs = sparkyFramework.getFrameworkVar('ip_allow_tsgs')
            if (!Array.isArray(ipAllowTSGs)) {
              return true
            }
            const parent_ids = _.get(tsgs.get(tsg_id), 'parent_ids', [])
            const show_ip_allow = !!(ipAllowTSGs?.includes(tsg_id) || _.intersection(ipAllowTSGs, parent_ids).length)
            return show_ip_allow && notAiopsFree() && !access?.hasRoleOnly?.(ASC_TIER_1_SUPPORT_ROLE)
          }
        }) : false
      ),
      sparkyFramework.getFrameworkVar('user_preferences_ui') && {
        key: 'preference',
        path: '/settings/preferences',
        title: 'User Preferences',
        expandable: false,
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: importComponent('UserPreferences'),
        mapStateToProps: ({ theme, preferences }: StoreState) => ({ theme: preferences?.user?.theme || theme }),
        ...commonServicesAvail,
      },
      vars.dev && {
        key: 'dev',
        path: '/dev',
        hidden: true,
        component: () => Promise.resolve({ default: () => null }),
        mapStateToProps: () => ({}),
      }
    ].filter(Boolean)

    const settings: NavItem[] = [{
      key: 'settings',
      path: '/settings',
      title: 'Settings',
      icon: SettingsIcon,
      expandable: true,
      mergeNav: true,
      children: pages,
    }]

    if (sparkyFramework.hasMicroApp(MSP_MICRO_APP)) {
      settings.push({
        key: 'msp-settings',
        mergeNav: true,
        title: 'Settings',
        icon: SettingsIcon,
        hidden: true,
        children: pages.map(item => ({ ...item, key: `msp-${item.key}`, path: `/msp${item.path}` }))
      } as NavItem)
    }

    return settings
  }

  return ({
    navigation: [
      ...getCommonServicePages(),
      ...(!isNetSec ? [{
        key: 'subscription',
        title: 'Subscription',
        path: '/setup',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        component: importComponent('SubscriptionFlowCheck'),
        hidden: true,
      }] : []),
      {
        key: 'profile',
        title: 'Profile',
        path: '/activation/profile',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        component: importComponent('Activation'),
        hidden: true,
      },
      ...(vars.unified_activation ? [{
        key: 'activation',
        title: 'Activation',
        path: '/activation',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        contentClassName: 'panwds-tw3 panwds-unset-reboot',
        mapStateToProps: commonModuleStateProps,
        component: importComponent('Activation'),
        hidden: true,
      }] : []),
      ...(inHub ? [{ // alias to /setup for hub2
        key: 'hub-subscription',
        title: 'Hub Subscription',
        path: '/hub/setup',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        component: importComponent('SubscriptionFlowCheck'),
        hidden: true,
      }, {
        key: 'hub',
        title: 'Hub',
        path: '/hub',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        contentClassName: 'panwds-tw3',
        mapStateToProps: hubStateProps,
        component: importComponent('Hub'),
        hidden: true,
      }, {
        key: 'products',
        path: '/marketplace',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        contentClassName: 'panwds-tw3',
        mapStateToProps: hubStateProps,
        component: importComponent('Hub'),
        hidden: true,
      }] : []),
      ...([{
        key: 'ztpdeviceactivation',
        title: 'ZTP Device Activation',
        path: '/ztpdeviceactivation',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        contentClassName: 'panwds-tw3',
        mapStateToProps: commonModuleStateProps,
        component: importComponent('ZTPDeviceActivation'),
        hidden: true,
      }])
    ].filter(Boolean) as NavItem[],
    actions: [
      {
        key: 'tenant-switcher',
        bottomMost: true,
        bypassTsgAvail: true,
        title: () => sparkyFramework.getAuthState()?.tsg_name || null,
        icon: importComponent('TsgNavIcon'),
        mapStateToProps: commonModuleStateProps,
        navComponent: sparkyFramework.getFrameworkVar('new_tenant_app_switcher') && sparkyFramework.getFrameworkVar('use_new_left_nav') ?
          importComponent('TenantAppSwitcher') : importComponent('TenantSwitcher')
      }
    ],
  })
}

export default microApp
